import DeviceSupport from "@components/atoms/DeviceSupport";
import Button from "@components/library/Button";
import { HumanloopMark } from "@components/library/HumanloopMark";
import { hlToast, ToastVariant } from "@components/library/Toast";
import { AuthSelector } from "@components/llama/Signup/AuthSelector";
import SignupForm from "@components/llama/Signup/SignupForm";
import SignupLayout, { HelpSnippet, SignupLoading } from "@components/llama/Signup/SignupLayout";
import CustomHead from "@components/templates/CustomHead";
import { MailIcon } from "@heroicons/react/outline";
import { useAuth } from "lib/use-auth";
import Link from "next/link";
import { useRouter } from "next/router";
import { ReactElement, useEffect, useState } from "react";
import { createUser, resendVerification } from "services/users.service";
import { useInterval } from "usehooks-ts";
import { LOGIN_URL, SIGNUP_WELCOME_URL } from "@/lib/constants";
import SSOForm from "@components/llama/Signup/SSOForm";

const SignUpPage: NextTypes.NextPageWithLayout = () => {
  const [showSignupForm, setShowSignupForm] = useState(false); // Whether to show the signup form or the auth selector
  const [showSSOForm, setShowSSOForm] = useState(false);
  const [signedUpEmail, setSignedUpEmail] = useState<string | null>(null); // The success signup
  const [resendCountdown, setResendCountdown] = useState(0);
  const { user: llamaUser } = useAuth();
  const router = useRouter();

  // Redirect to login if signup is disabled
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_ENABLE_SIGNUP === "false") {
      router.replace(LOGIN_URL);
    }
  }, [router]);

  const [initialValues, setInitialValues] = useState({
    email_address: "",
    password: "",
    full_name: "",
    email_subscribe: true,
  });

  // If email provided as query param, prefill the email field
  useEffect(() => {
    if (router.query.email) {
      setInitialValues((initialValues) => ({ ...initialValues, email_address: router.query.email as string }));
    }
  }, [router.query.email]);

  // To prevent the user from spamming the "Resend email" button,
  // we disable it for 60 seconds after the user clicks it (frontend only)
  useInterval(
    () => {
      setResendCountdown((v) => Math.max(v - 1, 0));
    },
    resendCountdown > 0 ? 1000 : null,
  );

  // Redirect if user is logged in (which also means user has been verified).
  if (llamaUser) {
    if (llamaUser.organizations.length > 0) {
      router.push(`/${llamaUser.organizations[0].organization.slug}`);
      return <SignupLoading text="Redirecting to app" />;
    } else {
      router.push(SIGNUP_WELCOME_URL);
      return <SignupLoading text="Continuing setup" />;
    }
  }
  return (
    <>
      {signedUpEmail ? (
        <main className="flex w-full flex-col items-center gap-24">
          <MailIcon className="h-32 w-32" />
          <h1 className="text-18-24 font-bold text-text-base-1">Please verify your email</h1>
          <span className="text-center text-14-20 text-text-base-2">
            Check <span className="font-bold">{signedUpEmail}</span> to verify your email and get started.
          </span>
          <div className="flex w-full flex-col items-center gap-16">
            <Button
              block
              size={40}
              onClick={() => {
                // We just set signedUpEmail to null to go back to the signup form.
                // We'll end up with unverified user in the database, so need to clean these up at some future time
                // but probably fine to just leave them there for now.
                setSignedUpEmail(null);
              }}
            >
              Use another email address
            </Button>
            <Button
              block
              size={40}
              onClick={async () => {
                try {
                  await resendVerification(signedUpEmail);
                  hlToast({
                    title: "Email sent",
                    description: `A verification email has been sent to ${signedUpEmail}.`,
                    variant: ToastVariant.Success,
                  });
                  setResendCountdown(60);
                } catch (e: any) {
                  console.log("Error resending verification email", e);
                  hlToast({
                    title: "Failed to send verification email",
                    description: e.response?.data?.detail || `Contact us for more details.`,
                    variant: ToastVariant.Error,
                  });
                }
              }}
              disabled={resendCountdown > 0}
            >
              {resendCountdown > 0 ? `Resend again in ${resendCountdown}s` : "Resend email"}
            </Button>
          </div>
          <HelpSnippet />
        </main>
      ) : (
        <div className="flex w-300 flex-col gap-40">
          <div className="flex flex-col items-center gap-24">
            <HumanloopMark className="h-32 w-32" />
            <div className="text-18-24 font-bold">Create your Humanloop account</div>
          </div>
          <div className="flex grow flex-col justify-start">
            {showSignupForm ? (
              <div className="mb-1">
                <SignupForm
                  initialValues={initialValues}
                  onBackClick={() => setShowSignupForm(false)}
                  onSubmit={async (values) => {
                    await createUser(values);
                    setSignedUpEmail(values.email_address);
                  }}
                />
              </div>
            ) : showSSOForm ? (
              <SSOForm onBackClick={() => setShowSSOForm(false)} initialValues={initialValues} signUp={true} />
            ) : (
              <AuthSelector role="Sign up" onSSO={() => setShowSSOForm(true)} onEmail={() => setShowSignupForm(true)} />
            )}
          </div>
          <div className="flex flex-col gap-24 text-center text-12-16 text-oldgray-700">
            <div>
              By creating an account, you agree to the{" "}
              <a
                className="text-oldblue-600"
                href="https://humanloop.com/policies/terms-of-service"
                target="_blank"
                rel="noreferrer"
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                className="text-oldblue-600"
                href="https://humanloop.com/policies/privacy-policy"
                target="_blank"
                rel="noreferrer"
              >
                Privacy Policy
              </a>
              .
            </div>
            <div>
              Already have an account?{" "}
              <Link href={LOGIN_URL} className="text-oldblue-600">
                Log in here
              </Link>
              .
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SignUpPage;

SignUpPage.getLayout = function getLayout(page: ReactElement) {
  return (
    <SignupLayout>
      <DeviceSupport />
      <CustomHead title="Sign up" description="Create your Humanloop account. Start for free." />
      {page}
    </SignupLayout>
  );
};
